import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CalendarClock, CheckCircle2, Clock, Copy, Plus, X } from 'lucide-react';
import Button from '../../components/Button';
import { WEEKDAYS } from '../../data/mockData';
import { useAuth } from '../../context/AuthContext';
import { getStoreHours } from '../../api/store';
import {
  friendlyWeeklySlotsError,
  getWeeklySlots,
  updateWeeklySlots,
} from '../../api/deliverySlots';
import shared from './StoreHoursCard.module.css';
import styles from './DeliverySlotsCard.module.css';

function toMinutes(value) {
  const [h, m] = value.split(':').map(Number);
  return h * 60 + m;
}

function formatTime(value) {
  const [h, m] = value.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = ((h + 11) % 12) + 1;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

function makeSlotKey() {
  return `k${Date.now()}${Math.random().toString(36).slice(2, 8)}`;
}

export default function DeliverySlotsCard() {
  const { user } = useAuth();
  const [hours, setHours] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [justSaved, setJustSaved] = useState(false);

  const [addingDay, setAddingDay] = useState(null);
  const [newSlot, setNewSlot] = useState({ start: '', end: '', label: '', maxOrders: '' });
  const [addError, setAddError] = useState('');

  const [copyFrom, setCopyFrom] = useState(null);
  const [copyTargets, setCopyTargets] = useState([]);
  const popoverRef = useRef(null);

  const dirty = useMemo(() => !!saved && !!draft && JSON.stringify(saved) !== JSON.stringify(draft), [saved, draft]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [store, weeklySlots] = await Promise.all([
        getStoreHours(user.idToken),
        getWeeklySlots(user.idToken),
      ]);
      setHours(store.weekly);
      setSaved(weeklySlots);
      setDraft(weeklySlots);
    } catch (err) {
      setLoadError(err.message || 'Could not load delivery slots.');
    } finally {
      setLoading(false);
    }
  }, [user.idToken]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!copyFrom) return undefined;
    const onClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setCopyFrom(null);
        setCopyTargets([]);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [copyFrom]);

  const startAdd = (day) => {
    setAddingDay(day);
    setNewSlot({ start: '', end: '', label: '', maxOrders: '' });
    setAddError('');
  };

  const cancelAdd = () => {
    setAddingDay(null);
    setAddError('');
  };

  const confirmAddSlot = (day) => {
    if (!newSlot.start || !newSlot.end) {
      setAddError('Pick both a start and end time.');
      return;
    }
    const startMin = toMinutes(newSlot.start);
    const endMin = toMinutes(newSlot.end);
    if (endMin <= startMin) {
      setAddError('End time must be after the start time.');
      return;
    }
    const dayHours = hours?.[day];
    if (dayHours?.open) {
      const openMin = toMinutes(dayHours.opensAt);
      const closeMin = toMinutes(dayHours.closesAt);
      if (startMin < openMin || endMin > closeMin) {
        setAddError(`Slot must fall within store hours (${formatTime(dayHours.opensAt)} – ${formatTime(dayHours.closesAt)}).`);
        return;
      }
    }
    const overlaps = (draft[day] || []).some(
      (s) => startMin < toMinutes(s.end) && endMin > toMinutes(s.start),
    );
    if (overlaps) {
      setAddError('This overlaps a slot that already exists.');
      return;
    }
    if (newSlot.maxOrders && (!Number.isInteger(Number(newSlot.maxOrders)) || Number(newSlot.maxOrders) < 1)) {
      setAddError('Max orders must be a whole number of at least 1.');
      return;
    }
    setDraft((prev) => ({
      ...prev,
      [day]: [
        ...(prev[day] || []),
        {
          _key: makeSlotKey(),
          templateId: null,
          start: newSlot.start,
          end: newSlot.end,
          label: newSlot.label.trim(),
          enabled: true,
          maxOrders: newSlot.maxOrders ? Number(newSlot.maxOrders) : null,
        },
      ].sort((a, b) => toMinutes(a.start) - toMinutes(b.start)),
    }));
    setAddingDay(null);
    setAddError('');
  };

  const removeSlot = (day, key) => {
    setDraft((prev) => ({ ...prev, [day]: (prev[day] || []).filter((s) => s._key !== key) }));
  };

  const toggleEnabled = (day, key) => {
    setDraft((prev) => ({
      ...prev,
      [day]: (prev[day] || []).map((s) => (s._key === key ? { ...s, enabled: !s.enabled } : s)),
    }));
  };

  const openCopyFor = (day) => {
    setCopyFrom(copyFrom === day ? null : day);
    setCopyTargets([]);
  };

  const toggleCopyTarget = (day) => {
    setCopyTargets((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
  };

  const applyCopy = () => {
    const source = draft[copyFrom] || [];
    setDraft((prev) => {
      const next = { ...prev };
      copyTargets.forEach((day) => {
        next[day] = source.map((s) => ({ ...s, _key: makeSlotKey(), templateId: null }));
      });
      return next;
    });
    setCopyFrom(null);
    setCopyTargets([]);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!dirty || saving) return;
    setSaving(true);
    setSaveError('');
    try {
      const weeklySlots = await updateWeeklySlots(draft, user.idToken);
      setSaved(weeklySlots);
      setDraft(weeklySlots);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2200);
    } catch (err) {
      setSaveError(friendlyWeeklySlotsError(err.message) || 'Could not save delivery slots.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={shared.card}>
      <div className={shared.head}>
        <span className={shared.iconWrap}><CalendarClock size={18} /></span>
        <div className={shared.headText}>
          <p className={shared.title}>Delivery slots</p>
          <p className={shared.subtitle}>Carve out delivery windows within each day's open hours — e.g. 7:00 PM – 8:00 PM.</p>
        </div>
      </div>

      <form className={shared.body} onSubmit={handleSave}>
        {loading && <p className={shared.hint}>Loading delivery slots…</p>}

        {!loading && loadError && (
          <div className={shared.hint}>
            <p className={shared.invalidWarning} style={{ margin: 0 }}>
              <AlertTriangle size={12} /> {loadError}
            </p>
            <Button type="button" variant="outline" size="sm" onClick={load} style={{ marginTop: 8 }}>
              Retry
            </Button>
          </div>
        )}

        {!loading && !loadError && draft && (
          <>
            <div className={shared.dayList}>
              {WEEKDAYS.map((day) => {
                const dayHours = hours[day];
                const daySlots = draft[day] || [];
                return (
                  <div className={shared.dayRow} key={day}>
                    <div className={shared.dayRowMain}>
                      <span className={shared.dayLabel}>{day}</span>
                      {dayHours.open ? (
                        <span className={styles.hoursRange}>
                          {formatTime(dayHours.opensAt)} – {formatTime(dayHours.closesAt)}
                        </span>
                      ) : (
                        <span className={shared.closedLabel}>Closed all day</span>
                      )}

                      {dayHours.open && (
                        <div className={shared.copyAnchor} style={{ marginLeft: 'auto' }}>
                          <button
                            type="button"
                            className={shared.copyBtn}
                            onClick={() => openCopyFor(day)}
                            aria-label={`Copy ${day}'s slots to other days`}
                            disabled={daySlots.length === 0}
                            title={daySlots.length === 0 ? 'Add a slot first' : `Copy ${day}'s slots to other days`}
                          >
                            <Copy size={13} />
                          </button>
                          {copyFrom === day && (
                            <div className={shared.popover} ref={popoverRef}>
                              <p className={shared.popoverTitle}>Copy {day}'s slots to</p>
                              <div className={shared.popoverDays}>
                                {WEEKDAYS.filter((x) => x !== day && hours[x]?.open).map((target) => (
                                  <label key={target} className={shared.popoverCheck}>
                                    <input
                                      type="checkbox"
                                      checked={copyTargets.includes(target)}
                                      onChange={() => toggleCopyTarget(target)}
                                    />
                                    {target}
                                  </label>
                                ))}
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                disabled={copyTargets.length === 0}
                                onClick={applyCopy}
                                className={shared.popoverApply}
                              >
                                Apply to {copyTargets.length || ''} day{copyTargets.length === 1 ? '' : 's'}
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {dayHours.open && (
                      <div className={styles.slotsArea}>
                        <div className={styles.slotChips}>
                          {daySlots.map((slot) => (
                            <span className={`${styles.slotChip} ${!slot.enabled ? styles.slotChipDisabled : ''}`} key={slot._key}>
                              <button
                                type="button"
                                className={styles.slotToggle}
                                onClick={() => toggleEnabled(day, slot._key)}
                                aria-label={slot.enabled ? 'Disable this slot' : 'Enable this slot'}
                                title={slot.enabled ? 'Disable this slot' : 'Enable this slot'}
                              >
                                <Clock size={11} />
                              </button>
                              {slot.label ? `${slot.label}: ` : ''}{formatTime(slot.start)} – {formatTime(slot.end)}
                              {slot.maxOrders ? ` (max ${slot.maxOrders})` : ''}
                              <button
                                type="button"
                                className={styles.slotRemove}
                                onClick={() => removeSlot(day, slot._key)}
                                aria-label={`Remove ${formatTime(slot.start)} – ${formatTime(slot.end)} slot`}
                              >
                                <X size={12} />
                              </button>
                            </span>
                          ))}
                          {daySlots.length === 0 && addingDay !== day && (
                            <span className={styles.noSlots}>No delivery slots yet</span>
                          )}
                        </div>

                        {addingDay === day ? (
                          <>
                            <div className={styles.addRow}>
                              <input
                                type="time"
                                className={shared.timeInput}
                                value={newSlot.start}
                                onChange={(e) => setNewSlot((s) => ({ ...s, start: e.target.value }))}
                              />
                              <span className={shared.timeSep}>–</span>
                              <input
                                type="time"
                                className={shared.timeInput}
                                value={newSlot.end}
                                onChange={(e) => setNewSlot((s) => ({ ...s, end: e.target.value }))}
                              />
                              <input
                                type="text"
                                placeholder="Label (optional)"
                                className={styles.labelInput}
                                value={newSlot.label}
                                onChange={(e) => setNewSlot((s) => ({ ...s, label: e.target.value }))}
                              />
                              <input
                                type="number"
                                min="1"
                                placeholder="Max orders"
                                className={styles.maxOrdersInput}
                                value={newSlot.maxOrders}
                                onChange={(e) => setNewSlot((s) => ({ ...s, maxOrders: e.target.value }))}
                              />
                              <Button type="button" size="sm" onClick={() => confirmAddSlot(day)}>Add</Button>
                              <Button type="button" size="sm" variant="ghost" onClick={cancelAdd}>Cancel</Button>
                            </div>
                            {addError && (
                              <p className={shared.invalidWarning} style={{ margin: '6px 0 0' }}>
                                <AlertTriangle size={12} /> {addError}
                              </p>
                            )}
                          </>
                        ) : (
                          <button type="button" className={styles.addSlotBtn} onClick={() => startAdd(day)}>
                            <Plus size={13} /> Add slot
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {saveError && (
              <p className={shared.invalidWarning} style={{ margin: '12px 0 0' }}>
                <AlertTriangle size={12} /> {saveError}
              </p>
            )}

            <Button type="submit" size="sm" disabled={!dirty || saving} className={shared.saveBtn} style={{ marginTop: 14 }}>
              {saving ? 'Saving…' : justSaved ? <><CheckCircle2 size={14} /> Saved</> : 'Save delivery slots'}
            </Button>
          </>
        )}
      </form>
    </div>
  );
}
